import * as Yup from "yup";
import { useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import useStyles from "../../../../styles/styles";
import keyLabel from "../../../../utils/keylabel";
import { IconTrash, IconPlus } from "@tabler/icons";
import { configState } from "../../../../recoil/atoms";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { InputField, SelectField } from "../../../../utils/FormFields";
import { Button, Grid, Divider, Typography, Box, Tooltip } from "@mui/material";
import { CreateNewTemplateRequest, InventoryTemplateApi, CreateNewTemplateRequestFieldsDataTypeEnum } from "../../../../api/medical-operations";

const dataType = keyLabel(CreateNewTemplateRequestFieldsDataTypeEnum);

interface Field {
  id: number | number;
  FieldName: string;
  DataType: string;
}

interface Collection {
  id: number;
  CollectionName: string,
  Fields: Field[],
}

export default function CreateTemplate() {
  const classes = useStyles();
  const config = useRecoilValue(configState);

  const [collections, setCollections] = useState<Collection[]>([{ id: 0, CollectionName: "", Fields: [{ id: 0, FieldName: "", DataType: "" }] }]);

  const handleAddCollection = () => {
    const newCollectionId = collections.length > 0 ? collections[collections.length - 1].id + 1 : 1;
    const newFieldId = 0;  
    setCollections([...collections, { id: newCollectionId, CollectionName: "", Fields: [{ id: newFieldId, FieldName: "", DataType: "" }] }]);
  };

  const handleAddField = (collectionId: any) => {
    const updatedCollections = collections.map((collection) => {
      if (collection.id === collectionId) {
        const newFieldId = collection.Fields.length > 0 ? collection.Fields[collection.Fields.length - 1].id + 1 : 1;
        return {
          ...collection,
          Fields: [
            ...collection.Fields,
            { id: newFieldId, FieldName: "", DataType: "" },
          ],
        };
      }
      return collection;
    });
    setCollections(updatedCollections);
  };

  const handleRemoveCollection = (id: number) => {
    const updatedCollections = collections.filter((collection) => collection.id !== id);
    setCollections(updatedCollections);
  };
  
  const handleRemoveField = (collectionId: number, fieldId: number) => {
    const updatedCollections = collections.map((collection) => {
      if (collection.id === collectionId) {
        const updatedFields = collection.Fields.filter((field) => field.id !== fieldId);
        
        return {
          ...collection,
          Fields: updatedFields,
        };
      }
      return collection;
    });                
    setCollections(updatedCollections);
  };
  
  const createNewTemplate = (values: CreateNewTemplateRequest) => {
    const template = new InventoryTemplateApi(config);
    template
      .createNewTemplate(values)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  return (
    <>
      <Formik        
        initialValues={{
          TemplateName: "",
          Description: "",          
          Collections: [
            {
              CollectionName: "",
              Fields: [{ FieldName: "", DataType: CreateNewTemplateRequestFieldsDataTypeEnum.Text }],
            },
          ],
        }}                        
        enableReinitialize={true}
        validationSchema = { Yup.object().shape({
          TemplateName: Yup.string().required("Template Name is required"),
          Description: Yup.string().required("Description is required"),
          Collections: Yup.array().of(
            Yup.object().shape({
              CollectionName: Yup.string().required("Collection Name is required"),
              Fields: Yup.array().of(
                Yup.object().shape({
                  FieldName: Yup.string().required("Field Name is required"),
                  DataType: Yup.string().when('$isDataTypeRequired', {
                    is: true,
                    then: Yup.string().required('Data Type is required'),
                  }),
                })
              ),
            })
          ),
        })}        
        onSubmit={(values) => {
          const transformedValues = {
            TemplateName: values.TemplateName,
            Description: values.Description,
            Collections: collections.map((collection) => ({
              CollectionName: values.Collections[collection.id].CollectionName,
              Fields: collection.Fields.map((field) => ({
                FieldName: values.Collections[collection.id].Fields[field.id].FieldName,
                DataType: values.Collections[collection.id].Fields[field.id].DataType.trim() !== ''
                  ? values.Collections[collection.id].Fields[field.id].DataType
                  : CreateNewTemplateRequestFieldsDataTypeEnum.Text,
              })),
            })),
          };          
          createNewTemplate(transformedValues);
        }}   
      >
        {({ isSubmitting }: any) => (
          <Form>
            <MainCard>
              <Grid container spacing={2}>
                <Typography variant="h3" sx={{ ml: 2.5, mt: 2 }}>
                 Create New Inventory Template
                </Typography>
                <Divider variant="middle" sx={{ mt: 2, width: "100%", borderColor: "rgba(0, 0, 0, 0.30)" }} />
                <Grid container xs={12} item justifyContent="center">
                  <Typography variant="h5" sx={{ ml: 2.5, mt: 2 }}>
                    Add Template Name and Description
                  </Typography>
                </Grid>
                <Grid container xs={12} item justifyContent="center">
                  <Grid item xs={8}>             
                    <InputField name="TemplateName" label="Template Name" fullWidth />
                  </Grid>  
                </Grid>                    
                <Grid container xs={12} item justifyContent="center">
                  <Grid item xs={8}>
                    <InputField name="Description" label="Description" fullWidth />
                  </Grid>  
                </Grid>              
                {collections.map((collection) => (
                  <Grid container item xs={12} spacing={2} key={collection.id}>
                    <Divider variant="middle" sx={{ mt: 2, width: "100%", borderColor: "rgba(0, 0, 0, 0.30)" }} />
                    <Grid item xs={12} sx={{ textAlign: "right" }}>
                      {collection.id !== 0 && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveCollection(collection.id)}
                        >
                          Remove Collection
                        </Button>
                      )}
                    </Grid>
                    <Grid container item xs={12} justifyContent="center">
                      <Grid item xs={12} sx={{ textAlign: "center" }}>
                        <Typography variant="h5" sx={{ ml: 2.5, mt: 2 }}>
                          Add Collection and Fields
                        </Typography>
                      </Grid>
                    </Grid>                                        
                    <Grid container item xs={12} justifyContent="center">
                      <Grid item xs={7} sx={{ pr: 1}}>
                        <InputField
                          name={`Collections[${collection.id}].CollectionName`}
                          label="Collection Name"
                          fullWidth                    
                        />
                      </Grid>
                      <Grid xs={0.96} justifyContent="center">
                        <Tooltip title="Add Field">
                          <Button
                            variant="outlined"
                            color="primary"
                            sx={{ mt: 1 }}
                            onClick={() => handleAddField(collection.id)}
                          >
                            <IconPlus />
                          </Button>
                        </Tooltip >  
                      </Grid>
                    </Grid>
                    <Grid container item xs={12} spacing={1.4} justifyContent="center">
                      <Grid item xs={5}>
                        <InputField name="FieldName" label="Name" fullWidth disabled />
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Box>
                          <SelectField name="CollectionDataType" label="Text" data={dataType} fullWidth disabled/>
                        </Box>
                      </Grid>
                      <Grid item xs={1}></Grid>
                    </Grid>
                    <Grid container item xs={12} spacing={1.4} justifyContent="center">
                      <Grid item xs={5}>
                        <InputField name="FieldName" label="Quantity" fullWidth disabled/>
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Box>
                          <SelectField name="CollectionDataType" label="Number" data={dataType} fullWidth disabled/>
                        </Box>
                      </Grid>
                      <Grid item xs={1}></Grid>
                    </Grid>
                    <Grid container item xs={12} spacing={1.4} justifyContent="center">
                      <Grid item xs={5}>
                        <InputField name="FieldName" label="Price" fullWidth disabled/>
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Box>
                          <SelectField name="CollectionDataType" label="Number" data={dataType} fullWidth disabled/>
                        </Box>
                      </Grid>
                      <Grid item xs={1}></Grid>
                    </Grid>
                    <Grid container item xs={12} spacing={1.4} justifyContent="center">
                      <Grid item xs={5}>
                        <InputField name="FieldName" label="Cost" fullWidth disabled/>
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Box>
                          <SelectField name="CollectionDataType" label="Number" data={dataType} fullWidth disabled/>
                        </Box>
                      </Grid>
                      <Grid item xs={1}></Grid>
                    </Grid>
                    {collection.Fields.map((field) => (
                      <Grid container item xs={12} spacing={2} key={field.id} justifyContent="center">
                        <Grid item xs={5}>
                          <InputField                            
                            name={`Collections[${collection.id}].Fields[${field.id}].FieldName`}
                            label="Field Name"
                            fullWidth                                                    
                          />
                        </Grid>
                        <Grid xs={2} sm={2}>
                          <Box sx={{ pl: 1, pt: 2 }}>                            
                            <SelectField
                              name={`Collections[${collection.id}].Fields[${field.id}].DataType`}
                              label="Data Type"
                              data={dataType}
                              type={CreateNewTemplateRequestFieldsDataTypeEnum}
                              fullWidth
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={1}>
                          {field.id !== 0 && (
                            <Tooltip title="Remove Field">
                              <Button
                                variant="outlined"
                                color="error"
                                sx={{ mt: 1 }}
                                onClick={() => handleRemoveField(collection.id, field.id)}                              
                              >
                                <IconTrash />
                              </Button>   
                            </Tooltip>                         
                          )}
                        </Grid>
                      </Grid>
                    ))}                    
                  </Grid>
                ))}                
                <Grid container xs={12} justifyContent="center" sx={{ pt: 4}}>
                  <Button variant="outlined" color="inherit" onClick={handleAddCollection} sx={{ width: '25%' }}>
                    Add Collection
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                  <Button type="submit" variant="contained" color="secondary" className={classes.button}>
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </MainCard>
          </Form>
        )}
      </Formik>
    </>
  );
}

